import React from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../helpers/useLocalStorage";
import { AuthContext } from "../../context/AuthContext";


export const AuthProvider = ({ children }) => {
    const [token, setToken] = useLocalStorage("token", null);
    const [user, setUser] = useLocalStorage("user", null);
    const [groups, setGroups] = useLocalStorage("groups", null);
    const [platform,setPlatform]= useLocalStorage("platform", null);
    const [platform_config,setPlatformConfig]= useLocalStorage("platform_config", null);
    // Backup of the real admin's own groups/platform/platform_config while
    // "View As" (Admin-only, see NavigationBar.js) is active - real_platform
    // being non-null is what marks a session as currently impersonating.
    // The real Cognito token never changes during View As: only the UI-facing
    // groups/platform/platform_config are swapped, so API calls still run
    // with the admin's real permissions while the UI renders as the chosen
    // platform would see it.
    const [real_groups, setRealGroups] = useLocalStorage("real_groups", null);
    const [real_platform, setRealPlatform] = useLocalStorage("real_platform", null);
    const [real_platform_config, setRealPlatformConfig] = useLocalStorage("real_platform_config", null);
    const navigate = useNavigate();

    // call this function when you want to authenticate the user
    const login = async (token,user,groups,platform,platform_config) => {
      setToken(token);
      setUser(user);
      setGroups(groups);
      setPlatform(platform);
      setPlatformConfig(platform_config);
      setRealGroups(null);
      setRealPlatform(null);
      setRealPlatformConfig(null);
      navigate("/");
    };

    const updatePlatformConfig = async (platform_config) => {
      setPlatformConfig(platform_config)
    }

    // Admin-only: temporarily render the UI as the given platform would see
    // it (real groups/config fetched from Cognito/Arango by the caller),
    // without logging out of the real admin session. Only backs up the real
    // identity once, so switching between View As targets without stopping
    // in between doesn't overwrite the backup with a previous impersonation.
    const view_as = (view_as_platform, view_as_groups, view_as_platform_config) => {
      if (real_platform === null) {
        setRealGroups(groups);
        setRealPlatform(platform);
        setRealPlatformConfig(platform_config);
      }
      setGroups(view_as_groups);
      setPlatform(view_as_platform);
      setPlatformConfig(view_as_platform_config);
    };

    const stop_view_as = () => {
      if (real_platform === null) {
        return;
      }
      setGroups(real_groups);
      setPlatform(real_platform);
      setPlatformConfig(real_platform_config);
      setRealGroups(null);
      setRealPlatform(null);
      setRealPlatformConfig(null);
    };

    // call this function to sign out logged in user
    const logout = () => {
      setToken(null);
      setUser(null);
      setGroups(null);
      setPlatform(null);
      setPlatformConfig(null);
      setRealGroups(null);
      setRealPlatform(null);
      setRealPlatformConfig(null);
      navigate("/", { replace: true });
    };

    const value = useMemo(
      () => ({
        token,
        login,
        logout,
        user,
        groups,
        platform,
        platform_config,
        updatePlatformConfig,
        view_as,
        stop_view_as,
        is_viewing_as: real_platform !== null,
        real_groups,
      }),
      [token,user,groups,platform,platform_config,real_groups,real_platform,real_platform_config]
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  };
  

  